import React, { useState } from "react";
import Frame from "../bench/Frame";
import Meta from "../bench/Meta";
import DotNav from "../bench/DotNav";
import brand from "../spec/brand";
import pillars from "../spec/pillars";
import craft from "../spec/craft";
import practice from "../spec/practice";
import sectors from "../spec/sectors";

const sectionMap = [
  { id: "intro",    label: "Studio" },
  { id: "pillars",  label: "Pillars" },
  { id: "craft",    label: "Craft" },
  { id: "practice", label: "Practice" },
  { id: "sectors",  label: "Sectors" },
  { id: "contact",  label: "Contact" },
];

const Home = () => {
  const [form, setForm] = useState({
    organisation: "",
    role: "",
    practice: "Custom Applications",
    sector: "Logistics & supply chain",
    timing: "Within the quarter",
    email: "",
    brief: "",
  });
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); setSent(true); };

  return (
    <Frame dotnav={<DotNav sections={sectionMap} />}>
      <Meta />

      {/* ── 1. Intro / Hero ─────────────────────────────────── */}
      <section id="intro" className="page-section page-section--intro">
        <div className="workbench">
          <header className="wordmark-head">
            <img className="wordmark-head__logo" src="/brand/ftd-logo.png" alt={brand.name} />
            <div className="wordmark-head__meta">
              <span>{brand.hq.city}, {brand.hq.country}</span>
              <span className="bullet">·</span>
              <span>Engineering studio</span>
              <span className="bullet">·</span>
              <span>est. {brand.hq.city}</span>
            </div>
          </header>

          <div className="intro-body">
            <h1 className="intro-title">
              Software <em>shaped</em> to the business.
            </h1>
            <p className="intro-lede">{brand.pitch}</p>

            <div className="intro-actions">
              <a href="#craft"   className="btn-solid">Review the craft →</a>
              <a href="#practice" className="btn-outline">Read the practice</a>
              <a href="#contact" className="btn-link">Start a brief →</a>
            </div>

            <div className="numbers-strip">
              {brand.numbers.map((n, i) => (
                <div className="numbers-card" key={i}>
                  <div className="numbers-card__v">{n.v}</div>
                  <div className="numbers-card__l">{n.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. Pillars ───────────────────────────────────────── */}
      <section id="pillars" className="page-section page-section--alt">
        <div className="workbench">
          <div className="page-header">
            <div>
              <div className="page-eyebrow">02 · PILLARS</div>
              <h2 className="page-title">A studio with a written posture.</h2>
            </div>
            <p className="page-lede">
              Four principles set the floor on how we engage. They appear in every brief we accept — and they
              are how an operator can tell quickly whether the studio is a fit.
            </p>
          </div>

          <div className="pillars">
            {pillars.map((p, i) => (
              <article className="pillar" key={p.key}>
                <span className="pillar__pin">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="pillar__title">{p.title}</h3>
                <p className="pillar__body">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Craft ─────────────────────────────────────────── */}
      <section id="craft" className="page-section">
        <div className="workbench">
          <div className="page-header">
            <div>
              <div className="page-eyebrow">03 · CRAFT · {String(craft.length).padStart(2, "0")} CAPABILITIES</div>
              <h2 className="page-title">What the bench actually builds.</h2>
            </div>
            <p className="page-lede">
              Each capability below is a real, recurring practice — staffed by the same senior team, with the
              same delivery discipline. Pick the one that fits the brief, or combine two; most engagements
              run a programme that crosses several.
            </p>
          </div>

          {craft.map((c) => (
            <article className="craft-spec" id={c.slug} key={c.slug}>
              <div className="craft-spec__grid">
                <div>
                  <div className="craft-spec__num">{c.code}</div>
                  <h3 className="craft-spec__title">{c.title}</h3>
                  <p className="craft-spec__short">{c.short}</p>
                </div>
                <div className="craft-spec__body">
                  <p>{c.body}</p>
                  <div className="craft-spec__deliverables">
                    <div className="craft-spec__deliverables-head">IN THE STATEMENT OF WORK</div>
                    <ul>
                      {c.deliverables.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  </div>
                  <div className="craft-spec__stack">
                    {c.stack.map((s, i) => <span key={i} className="craft-spec__stack-chip">{s}</span>)}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── 4. Practice ──────────────────────────────────────── */}
      <section id="practice" className="page-section page-section--alt">
        <div className="workbench">
          <div className="page-header">
            <div>
              <div className="page-eyebrow">04 · PRACTICE</div>
              <h2 className="page-title">Five stages. Written deliverables. No theatre.</h2>
            </div>
            <p className="page-lede">{practice.intro}</p>
          </div>

          {practice.stages.map((s) => (
            <article className="stage" id={s.key} key={s.key}>
              <div className="stage__n">{s.n}</div>
              <div>
                <h3 className="stage__title">{s.title}</h3>
                <p className="stage__body">{s.body}</p>
                <div className="stage__output">{s.output}</div>
              </div>
            </article>
          ))}

          <div className="engagement-note">
            <div className="page-eyebrow">ENGAGEMENT TYPES</div>
            <p>
              Fixed-scope engagements are sized in writing before the build opens — every stage above runs
              to a single defined outcome with a single contracted price. Retainer engagements are a
              contracted backlog: a known senior team, a known monthly cost, and a written backlog reviewed
              every two weeks.
            </p>
          </div>
        </div>
      </section>

      {/* ── 5. Sectors ───────────────────────────────────────── */}
      <section id="sectors" className="page-section">
        <div className="workbench">
          <div className="page-header">
            <div>
              <div className="page-eyebrow">05 · SECTORS · {String(sectors.length).padStart(2, "0")}</div>
              <h2 className="page-title">Operators we have built for.</h2>
            </div>
            <p className="page-lede">
              The studio's experience spans operating contexts where off-the-shelf software hits its limit
              quickly — logistics, manufacturing, financial back-office, regulated public-sector and the long
              tail of platforms where the integration layer is the real product.
            </p>
          </div>

          <div className="sector-list">
            {sectors.map((s, i) => (
              <div className="sector-row" key={i}>
                <div className="sector-row__n">{String(i + 1).padStart(2, "0")}</div>
                <div className="sector-row__name">{s.name}</div>
                <div className="sector-row__note">{s.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Contact ───────────────────────────────────────── */}
      <section id="contact" className="page-section page-section--alt">
        <div className="workbench">
          <div className="page-header">
            <div>
              <div className="page-eyebrow">06 · CONTACT</div>
              <h2 className="page-title">Start a brief.</h2>
            </div>
            <p className="page-lede">
              Initial conversations are at no charge, led by a senior engineer from the first call. Most
              briefs land within two weeks. We are honest, before either side signs, about whether the work
              suits a studio of our size — and we will say so.
            </p>
          </div>

          <div className="contact-bench">
            <div>
              {sent ? (
                <div className="brief-sent">
                  <div className="brief-sent__head">BRIEF RECEIVED · TICKET OPENED</div>
                  <p>
                    Thank you. A senior engineer at the {brand.hq.city} studio will write back within two
                    working days — either a scoping call, a written follow-up question, or a candid note
                    that the brief is not a fit. We do not let briefs go unanswered.
                  </p>
                </div>
              ) : (
                <form className="brief-form" onSubmit={submit}>
                  <label>
                    <span>Organisation</span>
                    <input required value={form.organisation} onChange={upd("organisation")} placeholder="Legal name and jurisdiction" />
                  </label>
                  <label>
                    <span>Your role</span>
                    <input value={form.role} onChange={upd("role")} placeholder="CTO · Head of Engineering · Product · Operations" />
                  </label>
                  <label>
                    <span>Practice of interest</span>
                    <select value={form.practice} onChange={upd("practice")}>
                      <option>Custom Applications</option>
                      <option>Mobile Engineering</option>
                      <option>System Integration</option>
                      <option>Cloud & DevOps</option>
                      <option>Product Audit & Architecture</option>
                      <option>Programme — multiple practices</option>
                    </select>
                  </label>
                  <label>
                    <span>Operating sector</span>
                    <select value={form.sector} onChange={upd("sector")}>
                      <option>Logistics & supply chain</option>
                      <option>Manufacturing</option>
                      <option>Financial services</option>
                      <option>Retail & e-commerce</option>
                      <option>Public sector & utilities</option>
                      <option>Healthcare</option>
                      <option>Telecommunications</option>
                      <option>Energy & resources</option>
                      <option>Education & research</option>
                      <option>Other</option>
                    </select>
                  </label>
                  <label>
                    <span>Timing</span>
                    <select value={form.timing} onChange={upd("timing")}>
                      <option>Within the month</option>
                      <option>Within the quarter</option>
                      <option>Within the half</option>
                      <option>Open-ended — exploring</option>
                    </select>
                  </label>
                  <label>
                    <span>Reply-to email</span>
                    <input required type="email" value={form.email} onChange={upd("email")} placeholder="you@organisation.com" />
                  </label>
                  <label>
                    <span>Brief</span>
                    <textarea value={form.brief} onChange={upd("brief")} placeholder="The problem you are trying to solve · the systems already in place · the timing you have in mind" />
                  </label>
                  <button type="submit" className="btn-solid">Send to the studio →</button>
                </form>
              )}
            </div>

            <aside className="contact-side">
              <div className="contact-side__head">Studio — {brand.hq.city}</div>
              <dl>
                <div>
                  <dt>General desk</dt>
                  <dd><a href={`mailto:${brand.contact.email}`}>{brand.contact.email}</a></dd>
                </div>
                <div>
                  <dt>Engagement finance</dt>
                  <dd><a href={`mailto:${brand.contact.finance}`}>{brand.contact.finance}</a></dd>
                </div>
                <div>
                  <dt>Office</dt>
                  <dd>{brand.hq.address.line1}<br />{brand.hq.address.line2}</dd>
                </div>
                <div>
                  <dt>Hours</dt>
                  <dd>{brand.contact.hours}</dd>
                </div>
                <div>
                  <dt>Timezone</dt>
                  <dd>{brand.hq.timezone} · UTC+2 / UTC+3</dd>
                </div>
                <div>
                  <dt>Response</dt>
                  <dd>Within two working days, always.</dd>
                </div>
              </dl>
            </aside>
          </div>
        </div>
      </section>
    </Frame>
  );
};

export default Home;
